import React, { Component } from 'react';
import _ from 'lodash';
import { MockService, PriorityMode } from 'types/rest/mockServiceTypes';
import { connect } from 'react-redux';
import { ApplicationState } from 'store/rootReducer';
import {
  expandTransactionAction,
  fetchTransactionsForShuttlePane,
  moveTransactions,
  reloadTransactionShuttle,
  resetSelectAllInShuttlePanes,
  updateShowPriorityModal,
  updateShuttleActivePage,
  updateShuttleSearchTerm,
  updateShuttleSorting,
  updateShuttleTransaction,
  updateShuttleTransactionSelection,
} from 'store/mockServices/mockServiceDetail/mockServiceDetailActions';
import { withTranslation, WithTranslation } from 'react-i18next';
import { PanePosition } from 'store/transactionShuttle/TransactionShuttleTypes';
import { isPrivateCloud } from 'utils/bzmUserConfigUtil';
import { collapseAllTemplates } from 'store/mockServiceTemplates/mockServiceTemplatesActions';
import { SelectAllType } from 'components/blazeTable/BlazeTable.types';
import { AssignmentActionType } from 'components/transactionsShuttle/TransactionShuttle.types';
import TransactionsShuttle from 'components/transactionsShuttle/TransactionsShuttle';
import { SortConfiguration } from 'types/rest/paginationTypes';
import { mockServicePatchAction } from 'store/mockServices/mockServiceList/mockServiceListActions';
import TransactionPriorityModal from './Modals/TransactionPriorityModal';
import { MockServiceTemplate } from '../../../../store/mockServiceTemplates/mockServiceTemplateTypes';
import { TransactionType } from '../../../../types/rest/assetCatalogTypes';
import AssignMessageFlowModal from './Modals/AssignMessageFlowModal';

type MockServiceTransactionShuttleProps = {
  mockService: MockService;
  disabled: boolean;
  /** Determines which type of transaction shuttle works with. */
  transactionType: TransactionType;
};

type AllProps = StateProps & DispatchProps & MockServiceTransactionShuttleProps & WithTranslation;

/**
 * Wrapper for the transaction shuttle connected to the Redux store. This is
 * @constructor
 */
class MockServiceTransactionShuttle extends Component<AllProps> {
  state = {
    showMessagingModal: false,
    moveTxId: null,
  };

  UNSAFE_componentWillReceiveProps(nextProps: Readonly<AllProps>): void {
    // Reload left pane only
    if (this.props.leftPaneControls.activePage !== nextProps.leftPaneControls.activePage) {
      this.props.fetchTransactionsForShuttlePane(this.props.mockService, 'LEFT', this.props.transactionType);
    }

    // Reload right pane only
    if (this.props.rightPaneControls.activePage !== nextProps.rightPaneControls.activePage) {
      this.props.fetchTransactionsForShuttlePane(this.props.mockService, 'RIGHT', this.props.transactionType);
    }
  }

  /**
   * Change pagination in one of the panes.
   * @param type
   * @param activePage
   */
  updateActivePageHandler = (type: PanePosition, activePage: number) => {
    this.props.updateShuttleActivePage(type, activePage);
  };

  /**
   * Filter in the pane has changed.
   * @param type
   * @param activePage
   */
  filterChangedHandler = _.debounce(async (type: PanePosition, searchTerm: string) => {
    this.props.updateShuttleSearchTerm(type, searchTerm);

    //Do load of the pane
    this.props.fetchTransactionsForShuttlePane(this.props.mockService, type, this.props.transactionType);
  }, 600);

  onHidePriorityModal = (updatedEntity: MockService | MockServiceTemplate) => {
    this.props.updateShowPriorityModalVisibility(false);
    if (updatedEntity) {
      //Reload pane and reset selection (if any)
      this.props.updateShuttleSorting('RIGHT', {
        sortKey: 'priority',
        direction: 'asc',
      });
      this.props.fetchTransactionsForShuttlePane(updatedEntity, 'RIGHT', this.props.transactionType);
      this.props.updateShuttleTransactionSelection('RIGHT', []);
    }
  };

  /**
   * Change of the transaction selection in the pane.
   * @param type - Pane position
   * @param transactionIds - List of transaction IDs
   * @param selectAllType - Type of select all action
   */
  transactionSelectionChangedHandler = (
    type: PanePosition,
    transactionIds: number[],
    selectAllType?: SelectAllType,
  ) => {
    this.props.updateShuttleTransactionSelection(type, transactionIds, selectAllType);
  };

  /**
   * Adds or removes transactions from / to virtual service based on an assignment type.
   * @param assignmentType - Type of assignment
   * @param txId - Transaction ID for single assignment
   * @param flowConfig - Flow configuration for messaging transactions
   */
  moveTransactions = async (assignmentType: AssignmentActionType, txId?: number, flowConfig?: string) => {
    await this.props.moveTransactions(
      this.props.mockService,
      assignmentType,
      this.props.transactionType,
      txId,
      flowConfig,
    );

    // Reset selection
    this.props.resetSelectAllInShuttlePanes();
    this.props.updateShuttleTransactionSelection('LEFT', []);
    this.props.updateShuttleTransactionSelection('RIGHT', []);
  };

  onSort = async (type: PanePosition, sort: SortConfiguration) => {
    //Save sorting
    this.props.updateShuttleSorting(type, sort);

    //Refresh
    this.props.fetchTransactionsForShuttlePane(this.props.mockService, type, this.props.transactionType);
  };

  /**
   * Expands item in the simple table.
   * @param type - Pane position
   * @param expandItem - Item to expand
   */
  expandTransaction = (type: PanePosition, expandItem: number | null) => {
    this.props.expandTransactionAction(type, expandItem);
  };

  componentDidMount() {
    this.props.getTransactionsForShuttle(this.props.mockService, this.props.transactionType);
  }

  /**
   * Reload both panes.
   */
  reloadPanes = async () => {
    //Do reload of the panes
    this.props.fetchTransactionsForShuttlePane(this.props.mockService, 'LEFT', this.props.transactionType);
    this.props.fetchTransactionsForShuttlePane(this.props.mockService, 'RIGHT', this.props.transactionType);

    // Reset selection
    this.props.resetSelectAllInShuttlePanes();
    this.props.updateShuttleTransactionSelection('LEFT', []);
    this.props.updateShuttleTransactionSelection('RIGHT', []);
  };

  onPriorityChanged = async (priorityMode: PriorityMode) => {
    const updated = _.cloneDeep(this.props.mockService);
    updated.priorityMode = priorityMode;
    await this.props.mockServicePatchAction(updated);
    if (priorityMode === PriorityMode.unique_priority) {
      this.props.updateShowPriorityModalVisibility(true);
    }
  };

  render() {
    // Prepare pane titles
    const leftShuttleTitle = this.props.t('transactionsShuttle.otherTransactions');
    const rightShuttleTitle = (
      <React.Fragment>
        {this.props.t('transactionsShuttle.transactionsInThis')} <strong>Virtual Service</strong>
      </React.Fragment>
    );

    return (
      <>
        {this.state.showMessagingModal && (
          <AssignMessageFlowModal
            onFinished={flowName => {
              const txnId = this.state.moveTxId;
              this.setState({ showMessagingModal: false, moveTxId: null });
              this.moveTransactions(AssignmentActionType.ADD, txnId, flowName);
            }}
            mockService={this.props.mockService}
            onHide={() => {
              this.setState({ showMessagingModal: false, moveTxId: null });
            }}
          />
        )}

        {this.props.rightPaneControls.showPriorityModal && (
          <TransactionPriorityModal
            context={this.props.mockService}
            onHide={this.onHidePriorityModal}
            type={this.props.transactionType}
          />
        )}
        <TransactionsShuttle
          leftPaneTitle={leftShuttleTitle}
          rightPaneTitle={rightShuttleTitle}
          disabled={this.props.disabled}
          isMoveLeftInProgress={this.props.isMoveLeftInProgress}
          isMoveRightInProgress={this.props.isMoveRightInProgress}
          context={this.props.mockService}
          leftPaneControls={this.props.leftPaneControls}
          rightPaneControls={this.props.rightPaneControls}
          leftPaneTransactions={this.props.leftPaneTransactions}
          rightPaneTransactions={this.props.rightPaneTransactions}
          onUpdateTransaction={(type, transaction) => {
            this.props.updateShuttleTransaction(type, transaction);
            // Collapse all templates since we change txn definition
            this.props.collapseAllTemplates();
          }}
          onSort={this.onSort}
          changeActivePageInPane={this.updateActivePageHandler}
          filterChangedInPane={this.filterChangedHandler}
          reloadHandler={this.reloadPanes}
          txnSelectionChangedInPane={this.transactionSelectionChangedHandler}
          moveTransactions={(assignmentType: AssignmentActionType, txId?: number) => {
            //Show modal if we are assigning messaging transaction
            if (this.props.transactionType === 'MESSAGING' && assignmentType === AssignmentActionType.ADD) {
              this.setState({ showMessagingModal: true, moveTxId: txId });
            } else {
              this.moveTransactions(assignmentType, txId);
            }
          }}
          expandItem={this.expandTransaction}
          isSearchDisabled={isPrivateCloud(this.props.blazeUserConfig)}
          onPriorityModeChanged={this.onPriorityChanged}
          onUpdateShowPriorityModalVisibility={isModalShown => {
            this.props.updateShowPriorityModalVisibility(isModalShown);
          }}
          transactionType={this.props.transactionType}
        />
      </>
    );
  }
}

const mapStateToProps = ({ mockServiceDetails, blazeUserConfig }: ApplicationState) => ({
  leftPaneControls: mockServiceDetails.txnShuttle.leftPane,
  rightPaneControls: mockServiceDetails.txnShuttle.rightPane,
  leftPaneTransactions: mockServiceDetails.txnShuttle.leftPaneTransactions,
  rightPaneTransactions: mockServiceDetails.txnShuttle.rightPaneTransactions,
  isMoveRightInProgress: mockServiceDetails.txnShuttle.isMoveRightInProgress,
  isMoveLeftInProgress: mockServiceDetails.txnShuttle.isMoveLeftInProgress,
  blazeUserConfig: blazeUserConfig.blazeUserConfig,
});

const mapDispatchToProps = {
  getTransactionsForShuttle: reloadTransactionShuttle,
  updateShuttleActivePage,
  updateShuttleTransactionSelection,
  updateShuttleTransaction,
  fetchTransactionsForShuttlePane,
  updateShuttleSearchTerm,
  moveTransactions,
  updateShuttleSorting,
  expandTransactionAction,
  collapseAllTemplates,
  resetSelectAllInShuttlePanes,
  mockServicePatchAction,
  updateShowPriorityModalVisibility: updateShowPriorityModal,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default withTranslation('mockServices')(
  connect(mapStateToProps, mapDispatchToProps)(MockServiceTransactionShuttle),
);
